import CaseStudy from '../components/CaseStudy';

const data = {
  company: 'Maqsad · Edtech · Pakistan',
  title: 'When OTP Fails:<br/>Designing an<br/>Onboarding That Works',
  subtitle: 'Redesigning Maqsad\'s login flow to cut drop-offs, slash costs, and gather the user data needed to personalise at scale.',
  metaRole: 'Product Thinking, UX/UI Design',
  metaPlatform: 'iOS & Android Mobile App',
  metaExtraLabel: 'Company Scale',
  metaExtra: '0 → 4M Users · PKR 12M ARR',
  outcomeValue: '↓22% drop-off',
  outcomeSub: 'OTP cost cut from PKR 200K → 40K/mo',
  heroPlaceholder: 'Hero image: Side-by-side of old OTP screen vs. new phone + password flow - shown on phone mockups, 2–3 screens wide',
  heroImage: '/onboarding-hero-img.png',
  metaItems: [
    { label: 'My Role', value: 'Product Thinking, UX/UI Design' },
    { label: 'Platform', value: 'iOS & Android Mobile App' },
    { label: 'Collaborators', value: 'PMs, Engineering, Support' },
    { label: 'Company Scale', value: '0 → 4M Users · PKR 12M ARR' },
  ],
  sections: [
    {
      type: 'text',
      label: 'Overview',
      heading: 'An OTP-only flow was silently failing millions of students.',
      paragraphs: [
        'Maqsad is Pakistan\'s leading edtech app, built for students preparing for O/A-Levels and board exams. At scale, a fundamental problem surfaced: the entire login system relied on OTP delivery - and for a large portion of our users, that simply didn\'t work.',
        'Students regularly used their parents\' numbers to register. In low-network areas across Pakistan, OTPs arrived late or not at all. Every failed login was a student who didn\'t get to study that day.',
      ],
    },
    {
      type: 'text',
      label: 'Problem',
      heading: 'Three problems. One broken flow.',
      paragraphs: [
        '<strong>Reliability:</strong> Students using parents\' numbers couldn\'t independently receive OTPs, creating constant login failures reported to customer support.',
        '<strong>Cost:</strong> At Maqsad\'s scale, OTP delivery was costing <strong>PKR 200,000 per month</strong> - a significant operational burden with no product benefit.',
        '<strong>Data blindspot:</strong> Beyond a phone number and grade, we knew almost nothing about our users. Personalisation was impossible and our sales team lacked context for outreach.',
      ],
      hmw: 'How might we reduce login friction and OTP dependency while actually learning more about our users in the process?',
    },
    {
      type: 'text',
      label: 'Solution',
      heading: 'A hybrid login + a single onboarding moment.',
      paragraphs: [
        'We explored email-password login but hit a technical wall - phone number was the primary key in our system. So instead of rebuilding the database, we layered a <strong>password on top of the existing phone verification</strong> and used the one-time OTP moment to collect the data we needed.',
        'New flow: users enter their phone and receive an OTP <em>once</em>, use that verification to set a password, then complete a short pre-entry form - name, email, academic level, school. Every future login is phone + password. No OTP ever again.',
      ],
    },
    {
      type: 'image',
      size: 'tall',
      src: '/onboarding-flow.jpg',
      text: 'Flow diagram or screen sequence: Phone entry → OTP (one time) → Password creation → Onboarding form → App entry',
      caption: 'The new onboarding flow - one OTP, then never again',
    },
    {
      type: 'image',
      size: 'medium',
      src: '/onboarding-flow.png',
      text: 'UI screens: The onboarding form - name, email, academic background, and school/college fields with your actual visual design',
      caption: 'The pre-entry onboarding form - collecting data that unlocks personalisation',
    },
    {
      type: 'text',
      label: 'Impact',
      heading: 'One redesign. Three measurable wins.',
      paragraphs: [],
      impact: [
        { num: '↓22%', label: 'Drop-off during login' },
        { num: '90%', label: 'Form completion rate pre-app entry' },
        { num: '↓80%', label: 'OTP cost - PKR 200K → 40K/mo' },
      ],
      afterImpact: [
        'Beyond the numbers: our sales team gained rich context for outreach, and the collected academic data became the foundation for personalisation features built in the following quarters.',
      ],
    },
    {
      type: 'text',
      label: 'Learnings',
      heading: 'A password layer unlocked more than security.',
      paragraphs: [
        'The biggest lesson: <strong>an onboarding form isn\'t a blocker if the value exchange is clear</strong>. Users didn\'t resist giving us their details - they just needed the form to feel purposeful and fast. When both were true, completion hit 90%.',
        'Working within technical constraints (phone as primary key) produced a better UX outcome than a full overhaul would have. The constraint forced a simpler, more elegant solution.',
      ],
    },
  ],
  nextTo: '/gamification',
  nextLabel: 'Next Project',
  nextTitle: 'Maqsad Sikkay: Designing for Retention',
};

export default function Onboarding() {
  return <CaseStudy data={data} />;
}
